/**
 * Faq — /faq public marketing page.
 *
 * Two-sided FAQ with clearly-labeled groups:
 *   - FOR DOG OWNERS  (9 questions)
 *   - FOR POOPARTNERS (6 questions)
 *
 * Accordion behavior:
 *   The codebase ships Shadcn's Radix-based <Accordion> at
 *   `/components/ui/accordion.jsx`. We use that as the "existing
 *   accordion component" — `type="multiple"` so customers can keep
 *   several answers open at once (more friendly than auto-collapse).
 *
 * Visual aesthetic mirrors the Pricing FAQ section:
 *   - Bold slate question heads
 *   - Soft body copy
 *   - Thin divider line between items
 *   - Generous vertical spacing
 *
 * Honesty: every answer is wired through `@/content/faq` — see the
 * module docstring for the live-claim guardrails (Stripe verification,
 * payouts, reviews are framed as "rolling out" / "at launch").
 */
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PublicPageShell from "@/components/PublicPageShell";
import BrandDivider from "@/components/BrandDivider";
import WinnerSlotCard from "@/components/WinnerSlotCard";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQ_HERO, FAQ_CUSTOMER, FAQ_PARTNER, FAQ_CTA } from "@/content/faq";

function FaqGroup({ groupKick, groupLabel, items, testIdPrefix }) {
  return (
    <section
      className="pp-wrap py-14 md:py-20"
      data-testid={`${testIdPrefix}-section`}
    >
      <div className="grid items-start gap-10 md:grid-cols-[1fr_1.6fr] md:gap-16">
        <header>
          <span
            className="pp-kick"
            data-testid={`${testIdPrefix}-kick`}
            style={{ color: "var(--pp-orange)" }}
          >
            {groupKick}
          </span>
          <h2
            className="pp-h2 mt-3"
            data-testid={`${testIdPrefix}-group-label`}
          >
            {groupLabel}
          </h2>
        </header>
        <Accordion
          type="multiple"
          className="border-t divide-y divide-pp-line"
          style={{ borderColor: "var(--pp-line)" }}
          data-testid={`${testIdPrefix}-accordion`}
        >
          {items.map((item, i) => {
            const idx = i + 1;
            const itemId = `${testIdPrefix}-item-${idx}`;
            return (
              <AccordionItem
                key={item.q}
                value={itemId}
                className="border-b-0"
                data-testid={itemId}
              >
                <AccordionTrigger
                  className="text-left text-[17px] font-bold py-5"
                  style={{ color: "var(--pp-slate)" }}
                  data-testid={`${itemId}-trigger`}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent
                  className="text-[15px] leading-relaxed"
                  style={{ color: "var(--pp-text-soft)" }}
                  data-testid={`${itemId}-content`}
                >
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </section>
  );
}

export default function Faq() {
  return (
    <PublicPageShell>
      <main data-testid="faq-page">
        {/* HERO BAND */}
        <section
          className="pp-wrap pt-14 md:pt-24 pb-6 md:pb-12"
          data-testid="faq-hero"
        >
          <span
            className="pp-kick"
            data-testid="faq-hero-kick"
            style={{ color: "var(--pp-purple)" }}
          >
            {FAQ_HERO.kick}
          </span>
          <h1
            className="pp-h1 mt-3 max-w-3xl"
            data-testid="faq-hero-headline"
          >
            {FAQ_HERO.headline}
          </h1>
          <p
            className="pp-sub mt-5 max-w-2xl"
            data-testid="faq-hero-sub"
          >
            {FAQ_HERO.sub}
          </p>
        </section>

        {/* CUSTOMER GROUP */}
        <BrandDivider testId="faq-divider-customer-header" />
        <FaqGroup
          groupKick={FAQ_CUSTOMER.groupKick}
          groupLabel={FAQ_CUSTOMER.groupLabel}
          items={FAQ_CUSTOMER.items}
          testIdPrefix="faq-customer"
        />

        {/* PARTNER GROUP — visually separated by a colored surface band */}
        <BrandDivider testId="faq-divider-partner-header" />
        <div style={{ background: "var(--pp-v3-surface)" }}>
          <FaqGroup
            groupKick={FAQ_PARTNER.groupKick}
            groupLabel={FAQ_PARTNER.groupLabel}
            items={FAQ_PARTNER.items}
            testIdPrefix="faq-partner"
          />
        </div>

        {/* CTA BAND — dual primary CTAs, matches /pricing pattern. */}
        <section
          style={{ background: "var(--pp-purple)" }}
          data-testid="faq-cta-section"
        >
          <div className="pp-wrap py-16 md:py-20 text-center">
            <h2
              className="text-white font-extrabold text-[28px] md:text-[40px] leading-tight max-w-3xl mx-auto"
              data-testid="faq-cta-headline"
            >
              {FAQ_CTA.headline}
            </h2>
            <p
              className="mt-4 text-white/85 text-[16px] md:text-[18px] max-w-xl mx-auto"
              data-testid="faq-cta-sub"
            >
              {FAQ_CTA.sub}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to={FAQ_CTA.customer_to}
                className="pp-btn"
                style={{ background: "white", color: "var(--pp-slate-2)" }}
                data-testid="faq-cta-customer"
              >
                {FAQ_CTA.customer_label}
                <ArrowRight size={14} aria-hidden />
              </Link>
              <Link
                to={FAQ_CTA.partner_to}
                className="pp-btn pp-btn-primary"
                data-testid="faq-cta-partner"
              >
                {FAQ_CTA.partner_label}
                <ArrowRight size={14} aria-hidden />
              </Link>
            </div>
          </div>
        </section>
        <WinnerSlotCard winnerRank={9} />
      </main>
    </PublicPageShell>
  );
}
